import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Download, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CopyButton } from "@/components/app/CopyLinkButton";
import { downloadBlob } from "@/lib/intake";

const NOTE = "Anyone with this link can take the questionnaire once";
const when = (d) => format(new Date(d), "d MMM yyyy, HH:mm");
const csvCell = (v) => `"${String(v).replace(/"/g, '""')}"`;

function Single({ l }) {
  return (
    <div className="space-y-3" data-testid="assessment-link-single">
      <label htmlFor="assessment-link-input" className="text-xs font-medium text-ink-secondary">Assessment link for {l.name}</label>
      <div className="flex flex-wrap items-center gap-2">
        <Input id="assessment-link-input" readOnly value={l.link} onFocus={(e) => e.target.select()} className="h-10 min-w-0 flex-1 bg-subtle font-mono text-xs" data-testid="assessment-link-input" />
        <CopyButton text={l.link} label="Copy link" testId="assessment-link-copy" />
      </div>
      <p className="text-xs text-ink-secondary" data-testid="assessment-link-expiry">{l.expires_at ? `Expires ${when(l.expires_at)}` : "Link doesn't expire · single use"}{l.reused && " · existing invite reused"}</p>
    </div>
  );
}

function Many({ links }) {
  const all = links.map((l) => `${l.name}: ${l.link}`).join("\n");
  const csv = () => downloadBlob(new Blob(["\ufeffname,link,expires_at\n" + links.map((l) => [l.name, l.link, l.expires_at].map(csvCell).join(",")).join("\n")], { type: "text/csv" }), "assessment-links.csv");
  return (
    <div className="space-y-3" data-testid="assessment-link-table">
      <div className="flex flex-wrap gap-2">
        <CopyButton text={all} label="Copy all" testId="assessment-links-copy-all" size="sm" />
        <Button size="sm" variant="outline" onClick={csv} data-testid="assessment-links-csv"><Download size={14} aria-hidden="true" /> Download CSV</Button>
      </div>
      <div className="max-h-80 overflow-y-auto rounded-[6px] border border-line">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-left text-xs text-ink-muted"><tr><th className="px-3 py-2 font-medium">Name</th><th className="px-3 py-2 font-medium">Link</th><th className="px-3 py-2" /></tr></thead>
          <tbody className="divide-y divide-line">
            {links.map((l) => (
              <tr key={l.assessment_id} data-testid={`assessment-link-row-${l.application_id}`}>
                <td className="whitespace-nowrap px-3 py-2 font-medium">{l.name}{l.reused && <span className="ml-1 text-xs font-normal text-ink-muted">(reused)</span>}</td>
                <td className="max-w-[260px] px-3 py-2"><input readOnly value={l.link} onFocus={(e) => e.target.select()} aria-label={`Link for ${l.name}`} className="w-full truncate bg-transparent font-mono text-xs text-ink-secondary outline-none" /></td>
                <td className="px-3 py-2 text-right"><CopyButton text={l.link} label="Copy" size="sm" testId={`assessment-link-copy-${l.application_id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-secondary">{links[0].expires_at ? `Links expire ${when(links[0].expires_at)} or when each invite reaches its expiry.` : "Links don't expire · each link is single use."}</p>
    </div>
  );
}

let pushLinks = null;
export const showLinks = (r) => pushLinks?.(r);

export function LinksHost() {
  const [r, setR] = useState(null);
  useEffect(() => { pushLinks = setR; return () => { pushLinks = null; }; }, []);
  return <AssessmentLinksDialog result={r} onClose={() => setR(null)} />;
}

export function AssessmentLinksDialog({ result, onClose }) {
  const links = result?.links || [];
  return (
    <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="assessment-links-dialog">
        <DialogHeader>
          <DialogTitle>{links.length > 1 ? `${links.length} assessment links` : "Assessment link"}</DialogTitle>
          <DialogDescription>No email was sent. Share the link with the candidate yourself.</DialogDescription>
        </DialogHeader>
        {links.length === 1 && <Single l={links[0]} />}
        {links.length > 1 && <Many links={links} />}
        {!!links.length && <p className="flex items-center gap-2 rounded-[6px] bg-caution-tint/60 px-3 py-2 text-xs text-caution" data-testid="assessment-link-note"><ShieldAlert size={14} aria-hidden="true" /> {NOTE}.</p>}
        {result?.skipped?.length > 0 && (
          <ul className="space-y-1 text-xs text-ink-secondary" data-testid="assessment-links-skipped">
            {result.skipped.map((s) => <li key={s.application_id}><strong className="text-ink">{s.name}</strong>: {s.reason}</li>)}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
